import { TextField } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import { useFormContext } from 'react-hook-form'
import { EanProps, EanType } from 'components/inputs/ean/types'
import { getFieldError, getFieldName } from 'components/inputs/utils'
import { FIELD_NAMES_AND_PARENTS } from 'components/inputs/ean/Ean.constants'
import { preventArrowUpAndDownEvent } from 'utils/eventHandlers'
import { isValidEAN } from 'utils/helpers'
import styles from './Ean.module.scss'

const Ean = ({ errorMessage, message, onChange, type }: EanProps) => {
  // Constants
  const { fieldName, parent } = FIELD_NAMES_AND_PARENTS[type]

  // React Hook Form
  const {
    formState: { errors },
    register,
    watch
  } = useFormContext()

  // i18n
  const { t } = useTranslation(['common', 'commonFormFields', 'validation'])

  /**
   * Returns if the given EAN is the same as the electricity EAN
   *
   * @param {string} value
   * @returns {boolean}
   */
  const isSameAsElectricityEan = (value: string): boolean => {
    const { fieldName: electricityFieldName, parent: electricityParent } = FIELD_NAMES_AND_PARENTS[EanType.ELECTRICITY]
    const electricityEan = watch(`${electricityParent}.${electricityFieldName}`)

    // Check if the EAN is the same as the electricity EAN
    return electricityEan && value && electricityEan === value
  }

  return (
    <TextField
      {...register(getFieldName(fieldName, parent), {
        onChange,
        required: t('validation:required'),
        validate: {
          ...(type !== EanType.ELECTRICITY && {
            unique: (value: number) => !isSameAsElectricityEan(value.toString(10)) || t('commonFormFields:ean.errors.sameAsElectricity')
          }),
          valid: (value: number) => isValidEAN(value.toString()) || t('commonFormFields:ean.errors.invalid')
        }
      })}
      className={styles['hide-arrows']}
      error={errorMessage || getFieldError(fieldName, parent, errors)?.message}
      grid="full"
      label={`${t('commonFormFields:ean.label')}${type === EanType.EXCL_NIGHT ? ` ${t('commonFormFields:ean.exclNight')}` : ''}${type === EanType.GAS ? ` ${t('common:gas').toLowerCase()}` : ''}`}
      {...(message && { message: { type: 'positive', text: message } })}
      onKeyDown={(e) => preventArrowUpAndDownEvent(e)}
      placeholder="54144..."
      type="number"
    />
  )
}

export default Ean
